import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Unit, LocationUnits, UnitService, get_space_estimate } from 'services/unit.service';
import { Router } from '@angular/router';
import { Location } from 'services/location.service';
import { Reservation, ReservationService } from 'services/reservation.service';
import { OTPService } from 'services/otp.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CaptchaService } from 'services/captcha.service';
import { environment } from 'src/environments/environment';
import { NewsletterService, NewsletterSubscriptionRequest } from 'services/newsletter.service';
import { CueWidgetService } from 'services/cue-widget.service';

@Component({
	selector: 'ras-reservation',
	templateUrl: './reservation.component.html',
	styleUrls: ['./reservation.component.sass']
})
export class ReservationComponent implements OnInit {

	@Input() location: Location;
	@Input() location_lookup: Location[];
	@Input() default: number;
	@Input() size: string;
	@Input() selected: number
	@Output() view_3d = new EventEmitter<[Unit, Unit | null, Unit | null]>();
	@Output() changed = new EventEmitter<null>();
	@Input() interactive_view_change: number;
	@Input('change') set change_set(difference: number) {
		if (difference == -1 || difference == 1) {
			setTimeout(() => {
				this.view_3d.emit(null);
				if (difference == -1) {
					this.change(-1);
				} else if (difference == 1) {
					this.change(1);
				}
				this.changed.emit();
				setTimeout(() => {
					this.view_3d.emit([this.selected_unit, this.next_unit(-1, true), this.next_unit(1, true)]);
				}, 1)
			}, 1)
		}
	}

	public captcha_passed = false
	public stage = 1
	public units: LocationUnits = null
	public unit_size_index = 0
	public selected_unit: Unit = null
	public cell_number: string = ''
	public entered_cell_number: string = ''
	public otp_sent: boolean = false
	public otp_digits = ['', '', '', '']
	public in_progress = false
	public nationality: string
	public first_name: string
	public last_name: string
	public email: string
	public id_number: string
	public tenant_type: string
	public move_in_date: Date
	public busy_checking = false
	public busy_cancelling = false
	public busy_reserving = false
	public busy_loading = false
	public busy_sending_otp = false
	public show_invalid = false
	public error_message: string = null
	public existing_reservations: Reservation[] = []
	public bouncing_cell_prompt = false
	private months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
	private bounce_timer = null

	public newsletter_signup = false;

	get_estimate(unit: Unit): string {
		return get_space_estimate(unit)
	}

	constructor(
		private reservationService: ReservationService,
		private unitService: UnitService,
		private otp: OTPService,
		private router: Router,
		private http: HttpClient,
		private captchaService: CaptchaService,
		private newsletterService: NewsletterService,
		private cueWidget: CueWidgetService
	) { }

	public format(date) {
		return (date == null) ? "" : ( date.getDate() + " " + this.months[date.getMonth()] + " " + date.getFullYear() )
	}

	bounce_cell_prompt() {
		if (!this.bounce_timer) {
			this.bouncing_cell_prompt = true
			this.bounce_timer = setTimeout(() => {
				this.bounce_timer = null
				this.bouncing_cell_prompt = false
			}, 600)
		}
	}

	select_unit(index) {
		this.selected_unit = this.units.all[index]
	}

	cancel_reservation(token) {
		this.in_progress = true
		this.busy_cancelling = true
		this.reservationService.cancel(token)
		.then(response => {
			this.busy_cancelling = false
			this.busy_checking = true
			this.reservationService.query(this.cell_number)
			.then(reservations => {
				this.existing_reservations = reservations
				this.busy_checking = false
				this.in_progress = false
			}, () => {
				this.existing_reservations = []
				this.busy_checking = false
				this.in_progress = false
			})
		}, (message) => {
			this.busy_cancelling = false
			this.busy_checking = true
			this.reservationService.query(this.cell_number)
			.then(reservations => {
				this.existing_reservations = reservations
				this.busy_checking = false
				this.in_progress = false
			}, () => {
				this.existing_reservations = []
				this.busy_checking = false
				this.in_progress = false
			})
			console.log("Error cancelling reservation.", message)
			// TODO error message when cancelling reservation fails
		})
	}

	reserve() {
		// Always show validation state when reserve is clicked
		this.show_invalid = true

		if (!this.valid_details()) {
			// Validation failed - don't proceed
			return
		}

		this.busy_reserving = true

		if (this.nationality != 'sa') {
			this.log_reservation()
			// Handle newsletter subscription before navigation
			this.handleNewsletterSubscription();
			this.router.navigate(['/foreign-national', this.location.key])
		} else {
			if ((<any>window)?.dataLayer) (<any>window).dataLayer.push({
				'event': 'unit_reserved',
				'price': this.units.all[this.unit_size_index].rate,
				'unit_size': this.units.all[this.unit_size_index].dim,
				'facility_name': this.location.name
			});

			this.reservationService.reserve(this.units.all[this.unit_size_index].id, this.first_name, this.last_name, this.cell_number, this.email, this.id_number, this.tenant_type == 'business', this.move_in_date, this.location.id, this.location.name)
			.then(async (token) => {
				// No need to log_reservation() here - the reserve() call already creates the ticket
				// Handle newsletter subscription after successful reservation
				await this.handleNewsletterSubscription();

				// Send reservation data to Cue WhatsApp widget
				this.cueWidget.setReservationData(
					token,
					this.units.all[this.unit_size_index].id,
					this.location.name
				);

				let reservation = {
					location: this.location.key,
					token: token,
					email: this.email
				}
				this.router.navigate(['/reserved', btoa(JSON.stringify(reservation))])
			}, (message) => {
				this.busy_reserving = false
				this.error_message = message
			})
		}
	}

	valid_id_number() {
		// ID number is only required for SA citizens
		if (this.nationality !== 'sa') {
			return true
		}

		// Check if ID number is provided
		if (!this.id_number) {
			return false
		}

		// Must be exactly 13 digits
		if (!(/^\d{13}$/).test(this.id_number)) {
			return false
		}

		// Validate first 6 digits as YYMMDD date of birth
		const year = parseInt(this.id_number.substring(0, 2), 10)
		const month = parseInt(this.id_number.substring(2, 4), 10)
		const day = parseInt(this.id_number.substring(4, 6), 10)

		// Month must be 01-12
		if (month < 1 || month > 12) {
			return false
		}

		// Day must be valid for the month
		// Using a year in the 1900s or 2000s for date validation
		const fullYear = year >= 0 && year <= 25 ? 2000 + year : 1900 + year
		const daysInMonth = new Date(fullYear, month, 0).getDate()
		if (day < 1 || day > daysInMonth) {
			return false
		}

		return true
	}

	id_number_error_message(): string {
		if (this.nationality !== 'sa') {
			return ''
		}
		if (!this.id_number) {
			return 'ID number is required'
		}
		if (!(/^\d{13}$/).test(this.id_number)) {
			return 'ID number must be exactly 13 digits'
		}
		// If we get here, the date validation failed
		return 'Invalid date of birth in ID number'
	}

	valid_email() {
		return (/^[a-zA-Z0-9-]+(?:[+.][a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/).test(this.email)
	}

	valid_first_name() {
		return !!this.first_name
	}

	valid_last_name() {
		return !!this.last_name
	}

	valid_nationality() {
		return this.nationality === 'sa' || this.nationality === 'foreign'
	}

	valid_tenant_type() {
		return this.tenant_type == 'individual' || this.tenant_type == 'business'
	}

	valid_move_in_date() {
		let now = (new Date()).getTime()
		if (!this.move_in_date || this.move_in_date.getTime() <= (now - 24*60*60*1000)) return false
		if (this.location?.earliestMoveIn && this.move_in_date.getTime() < this.location.earliestMoveIn.getTime()) return false
		return true
	}

	valid_details() {
		return this.valid_first_name() && this.valid_last_name() && this.valid_nationality() && this.valid_move_in_date() && this.valid_email() && this.valid_id_number()
	}

	valid_phone_number() {
		return (/^\(?(?:\+?27|0)[1-9][0-9]\)?(?:[ -]?[0-9]){7}$/.test(this.entered_cell_number.toString()))
	}

	tried_phone_number() {
		return this.entered_cell_number?.toString()?.length > 0;
	}

	ngOnInit() {
		if (window && window.sessionStorage) {
			try {
				const qdetails = window.sessionStorage.getItem("qdetails");
				if (qdetails) {
					const details = JSON.parse(atob(qdetails));
					this.entered_cell_number = details.cell;
					this.email = details.email;
					this.first_name = details.first_name;
					this.last_name = details.last_name;
					this.newsletter_signup = details.newsletter_signup || false; // Add this line
				}
			} catch (error) {
				// do nothing on exception
			}
		}

		this.busy_loading = true
		this.unitService.get_units(this.location.id)
		.then(data => {
			this.busy_loading = false
			this.units = data

			// Send unit browsing data to Cue WhatsApp widget
			this.cueWidget.setUnitBrowsingData(this.location.name, this.size)

			// pick unit by ID if it exists and has one day only special
			let [unit] = this.units.all.filter(unit => unit.id == this.default && unit.on_sale);
			if (!!unit) {
				console.log("UNIT FOUNd", unit)
				this.unit_size_index = unit.index
				this.selected_unit = unit
				return
			}

			unit = cheapest_unit(this.units.all, true)
			if (unit) {
				this.unit_size_index = unit.index
				this.selected_unit = unit
			}

			// select default unit by size
			if (this.size in this.units.by_size && this.units.by_size[this.size].length > 0) {
				let unit = cheapest_unit(this.units.by_size[this.size])
				if (unit) {
					this.unit_size_index = unit.index
					this.selected_unit = unit
				}
			}

			// select default unit by id
			else if (this.default > 0) {
				for (let unit of this.units.all) {
					if (unit.id == this.default) {
						this.unit_size_index = unit.index
						this.selected_unit = unit
						break
					}
				}
			}

		}, () => {
			this.busy_loading = false
			this.error_message = "Sorry, it seems that we can't communicate with our server right now. Please try again later."
		})

		function cheapest_unit(units: Unit[], no_parking: boolean = false): Unit {
			let best = null
			for (let unit of units) {
				if (best == null || unit.rate < best.rate) {
					if (!no_parking || !unit.parking) best = unit
				}
			}
			return best
		}
	}

	clear_otp() {
		if (Array.prototype.fill) {
			this.otp_digits.fill('')
		} else this.otp_digits = ['','','','']
	}

	resend_otp() {
		this.clear_otp()
		this.otp_sent = false
	}

	back_to_stage(stage) {
		switch (stage) {
			case 1:
				this.otp_sent = false
				this.stage = 1
				this.existing_reservations = []
				this.clear_otp()
				break
			case 2:
				this.stage = 2
				this.in_progress = true
				this.busy_checking = true
				this.reservationService.query(this.cell_number)
				.then(reservations => {
					this.existing_reservations = reservations
					this.in_progress = false
					this.busy_checking = false
				}, () => {
					this.existing_reservations = []
					this.in_progress = false
					this.busy_checking = false
				})
		}
	}

	async initiate_captcha() {
		this.show_invalid = true

		// validate
		if (!this.valid_phone_number() || !this.valid_email() || !this.valid_first_name() || !this.valid_last_name()) {
			return;
		}

		// save form data to session
		if (window && window.sessionStorage) {
			const qdetails = btoa(JSON.stringify({
				cell: this.entered_cell_number,
				email: this.email,
				first_name: this.first_name,
				last_name: this.last_name,
				newsletter_signup: this.newsletter_signup // Add this line
			}));
			window.sessionStorage.setItem("qdetails", qdetails);
		}

		// run captcha check
		this.busy_sending_otp = true
		this.captchaService.on_ready(
			(site_key: string) => {
				// this.captcha_ready = true; console.log("CAPTCHA READY")
				window["grecaptcha"].execute(site_key, { action: 'submit' }).then(async token => {
					// token received successfully.
					// const res = await this.captchaService.verifyToken(token) //TODO: fix CORS issue that stops this query
					this.request_otp(token);
				})
			}
		)
	}

	// Add this method to handle newsletter subscription
	private async handleNewsletterSubscription(): Promise<void> {
		if (!this.newsletter_signup || !this.email) {
			return; // Skip if not opted in or no email
		}

		try {
			const subscriptionRequest: NewsletterSubscriptionRequest = {
				email: this.email,
				firstName: this.first_name,
				lastName: this.last_name,
				source: 'reservation_form'
			};

			const result = await this.newsletterService.subscribe(subscriptionRequest);

			if (!result.success) {
				console.warn('Newsletter subscription failed:', result.message);
				// Optionally show a non-blocking warning to the user
			}
		} catch (error) {
			console.error('Newsletter subscription error:', error);
			// Don't block the main flow for newsletter failures
		}
	}

	request_otp(token: string) {
		this.busy_sending_otp = true
		this.clear_otp()

		if (this.valid_phone_number()) {
			this.otp.generate(this.entered_cell_number, token)
			.then(
				async () => {
					this.otp_sent = true
					this.busy_sending_otp = false
					this.show_invalid = false
					// Handle newsletter subscription after successful OTP generation
					await this.handleNewsletterSubscription();
					// once OTP for new Unit request is sent, silently cancel previous reservations.
					this.reservationService.query(this.entered_cell_number).then(reservations => {
						if (reservations?.length > 0) {
							this.existing_reservations = reservations
							this.existing_reservations?.forEach(reservation => {
								this.cancel_reservation(reservation?.token);
							})
						}
					}, () => {
						this.existing_reservations = []
					})

					if ((<any>window).dataLayer) (<any>window).dataLayer.push({
						'event': 'initiate-checkout',
						'unit_size': this.units.all[this.unit_size_index].dim,
						'facility_name': this.location.name
					});
				}, (message) => {
					this.error_message = "Sorry, we cannot contact the OTP server to confirm your phone number at this time. Please try again later."
				}
			)
			.catch(
				() => {
					this.busy_sending_otp = false
				}
			)
		}

		if ((<any>window).dataLayer) (<any>window).dataLayer.push({
			'event': 'get_price',
			'unit_size': this.units.all[this.unit_size_index].dim,
			'facility_name': this.location.name
		});
	}

	next_unit(difference, no_parking = false) {
		if (!(difference < 0 || difference >= 0)) return null
		if (difference == 0) return this.selected_unit

		function next_size(size, difference) {
			switch (size) {
				case 'small': return (difference > 0) ? 'medium' : null
				case 'medium': return (difference > 0) ? 'large' : 'small'
				case 'large': return (difference > 0) ? (no_parking ? null : 'parking') : 'medium'
				case 'parking': return (difference > 0) ? null : 'large'
				default: return null
			}
		}

		let unit = this.selected_unit
		let index = unit.by_size_index
		var size = unit.sizerank
		var next_index = index + difference
		var units = this.units.by_size[size]

		while (next_index < 0 || next_index >= units.length) {
			size = next_size(size, difference)
			if (size == null) return null
			units = this.units.by_size[size]
			next_index = difference > 0 ? 0 : units.length - 1
		}

		return units[next_index]
	}

	change(difference, same_size = false) {

		let next_unit = this.next_unit(difference)

		if (next_unit) {
			this.selected_unit = next_unit
			this.unit_size_index = this.selected_unit.index
		}
	}

	log_price_request() {
		this.http
		.post<any>(
			`${environment.portalBaseUrl}/api/server/price-request`,
			{
				branch: this.location.name,
				action: "price",
				cell: this.cell_number,
				rate: this.selected_unit.rate,
				unit: this.selected_unit.dim + ": " + this.selected_unit.area + "m² | " + this.selected_unit.desc,
				first_name: this.first_name,
				last_name: this.last_name,
				email: this.email,
				specials: this.selected_unit.first_month_free ? "One month free"
					: this.selected_unit.three_month_half_price ? "50% off first 3 months"
					: this.selected_unit.one_month_half_price ? "50% off first month"
					: ""
			},
			{
				headers: new HttpHeaders ({
				}),
			}
		)
		.toPromise()
		.then(response => {
		}, reason => console.log("Problem logging price request.", reason))
	}

	log_reservation() {
		this.http
		.post<any>(
			`${environment.portalBaseUrl}/api/server/price-request`,
			{
				branch: this.location.name,
				action: "reservation",
				cell: this.cell_number,
				rate: this.selected_unit.rate,
				unit: this.selected_unit.dim + ": " + this.selected_unit.area + "m² | " + this.selected_unit.desc,
				nationality: this.nationality,
				first_name: this.first_name,
				last_name: this.last_name,
				email: this.email,
				date: this.move_in_date,
			},
			{
				headers: new HttpHeaders ({
				}),
			}
		)
		.toPromise()
		.then(response => {
		}, reason => console.log("Problem logging reservation.", reason))
	}

	accept_cell_number() {
		if (this.cell_number != this.entered_cell_number) {
			for (let unit of this.units.all) {
				unit.unlocked = false
			}
		}

		this.cell_number = this.entered_cell_number
		.replace(/[- ()+]/g, '')
		.replace(/^27/, '0')
	}

	skip_otp() {
			this.otp_sent = false
			this.accept_cell_number();

			for (let unit of this.units.all) unit.unlocked = true;
			// this.selected_unit.unlocked = true
			// if (this.unit_size_index > 0) this.units.all[this.unit_size_index - 1].unlocked = true
			// if (this.unit_size_index < this.units.all.length - 1) this.units.all[this.unit_size_index + 1].unlocked = true

			this.show_price()
	}

	show_price() {
			this.stage = 2
			this.reservationService.query(this.cell_number)
	}

	reserve_now() {
		if ((<any>window)?.dataLayer) (<any>window).dataLayer.push({
			'event': 'clicked_reserve_now'
		});

		// once OTP for new Unit request is sent, silently cancel previous reservations.
		this.reservationService.query(this.entered_cell_number).then(reservations => {
			reservations?.forEach(reservation => {
				this.cancel_reservation(reservation?.token);
			})
			this.existing_reservations = []
			this.stage = 3
		}, () => {
			this.stage = 3
			this.existing_reservations = []
		})
	}

	view_3d_click() {
		if ((<any>window)?.dataLayer) (<any>window).dataLayer.push({
			'event': 'view_3d'
		});

		this.view_3d.emit([this.selected_unit, this.next_unit(-1, true), this.next_unit(1, true)])
	}

	otp_input(event, index) {

		// check if all the digits are complete
		let all_digits = true
		for (let digit of this.otp_digits) {
			if (digit == '') all_digits = false
		}

		// focus the next input box
		let next_sibling = event.target.nextElementSibling
		if (next_sibling) next_sibling.focus()

		// if all digits are entered, test the OTP and continue
		if (all_digits) {
			if ((<any>window)?.dataLayer) (<any>window).dataLayer.push({
				'event': 'otp_entered'
			});

			this.in_progress = true
			this.otp.verify(this.entered_cell_number, this.otp_digits.join(''))
			.then(() => {
				this.in_progress = false
				this.otp_sent = false
				this.accept_cell_number()

				for (let unit of this.units.all) unit.unlocked = true;
				// this.selected_unit.unlocked = true
				// if (this.unit_size_index > 0) this.units.all[this.unit_size_index - 1].unlocked = true
				// if (this.unit_size_index < this.units.all.length - 1) this.units.all[this.unit_size_index + 1].unlocked = true

				this.log_price_request()
				this.show_price()

			}, (reason) => {
				for (let i = 0; i < 4; i += 1) this.otp_digits[i] = ''
				this.in_progress = false
			})
		}

		function find_size_index(units: Unit[], index: number) {
			for (let i in units) {
				if (units[i].index == index) return Number.parseInt(i)
			}
		}
	}

	 is_open_parking(unit) {
		return (
		  unit.desc.search("Open Parking") >= 0 ||
		  unit.desc.search("Open Air Parking") >= 0
		);
	  }
}
